/**
 * contracts.md §11 — validation primitives (D57).
 *
 * Route schemas live beside their handlers. **The primitives below are imported, never
 * copied** — a second local implementation of the display-text policy is how one surface
 * ends up accepting a bidi override that another rejects.
 *
 * **Export hardening (§7.6 CSV) is a rendering concern, not a validation one** and lives
 * with the export writer (P15): a leading `=`, `+`, `-`, `@`, TAB, or CR is neutralized at
 * write time. It is named here so no PRD assumes validation already removed it — it
 * deliberately does not, because `=cmd()` is a legitimate display name and must round-trip
 * intact everywhere except a spreadsheet cell.
 *
 * Every character class below is written with `\u` escapes on purpose: a rule about
 * invisible characters must be reviewable, and a literal U+202E in the source is not.
 */
import { asE164 } from './keys.js';
/**
 * 422 with the offending field named — and the rejected VALUE never echoed.
 * The never-echo/never-log rule is this type's behavior, not a caller's discipline:
 * `toJSON()`/`toString()` expose the field path and the rule, never the input.
 */
export class ValidationError extends Error {
    status = 422;
    field;
    rule;
    constructor(field, rule) {
        super(`${field}: ${rule}`);
        this.name = 'ValidationError';
        this.field = field;
        this.rule = rule;
    }
    toString() {
        // `this.name`, never the literal: a subclass below is a different answer and must say so
        // here too. For `ValidationError` itself the rendered string is byte-identical.
        return `${this.name}: ${this.field}: ${this.rule}`;
    }
    toJSON() {
        return { status: this.status, field: this.field, rule: this.rule };
    }
}
/**
 * The country calling codes this product will accept a phone number in.
 *
 * **One entry, and the product is the reason rather than a threat model.** SeloGate is
 * Israel-only — **D256** resolves the market to Israeli law alone, and `AGENTS.md` §4.2 buys
 * Israeli SMS segments from one Israeli provider — so a number outside this list is a
 * destination the product has no reason to reach and a standing reason to refuse: an OTP is
 * sent on `POST /auth/otp/start` **before any account exists**, D117's per-segment ILS rate is
 * a real per-message cost, `SmsEstimatedSpend` is an **alarm and not a breaker**, and the
 * D65/D88/D248 caps bound **one number** rather than the set of numbers an enumerating caller
 * can supply.
 *
 * **Widening it is one edit here and one national-shape rule in `normalizePhone`** — a calling
 * code added without its subscriber rule re-opens, for that country, exactly the shape hole
 * this list closes for everything but `+972`.
 */
export const SUPPORTED_COUNTRY_CALLING_CODES = ['972'];
/** `+972`, `+1`, … — the rule text, derived so it stays true when the list above widens. */
function supportedCountriesRule() {
    return `must be a phone number in a country this service operates in (${SUPPORTED_COUNTRY_CALLING_CODES.map((code) => `+${code}`).join(', ')})`;
}
/**
 * A well-formed E.164 number in a country this product does not serve.
 *
 * **It extends `ValidationError` rather than standing beside it, and both halves of that are
 * deliberate.** Extending keeps every existing handler correct without an edit — the auth and
 * admin routers render it `422` by `instanceof`, `members/bulk.ts` files the row as `invalid`
 * instead of rethrowing into a `500`, and the client's `PhoneStep`/`AuthProvider` treat it as
 * *not sendable yet* instead of logging a normalizer fault. Being its **own type** is what
 * lets a caller tell it apart at all: *that is not a phone number* and *we do not serve that
 * country* are different answers to a resident, to a manager reading a bulk-invite row, and to
 * a caller reading the `422` — whose `rule` is the discriminator on the wire.
 *
 * **The refused number is not echoed and the refused country is not named**, on
 * `ValidationError`'s own rule: what the error carries is the list we *do* serve, which the
 * caller supplied nothing of.
 */
export class UnsupportedCountryError extends ValidationError {
    /** The codes that would have been accepted — never the one that was refused. */
    supportedCountryCallingCodes = SUPPORTED_COUNTRY_CALLING_CODES;
    constructor(field) {
        super(field, supportedCountriesRule());
        // After `super`, which sets the base name.
        this.name = 'UnsupportedCountryError';
    }
}
/** Declared-schema validation: unknown fields are rejected, not stripped. */
export function parseBody(schema, raw) {
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
        throw new ValidationError('body', 'must be a JSON object');
    }
    const declared = new Set(schema.fields);
    for (const key of Object.keys(raw)) {
        if (!declared.has(key)) {
            // The field NAME is the offending thing here, and naming it is the whole point of
            // the 422. The value it carried is never touched.
            throw new ValidationError(key, 'unknown field');
        }
    }
    return schema.parse(raw);
}
/** C0 (U+0000–U+001F) and C1 (U+007F–U+009F) control characters. */
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/u;
/**
 * Bidi controls submitted as content: LRM/RLM (U+200E/U+200F), the embedding and override
 * set LRE–RLO (U+202A–U+202E), the isolates LRI–PDI (U+2066–U+2069), and ALM (U+061C).
 */
const BIDI_CONTROLS = /[\u200E\u200F\u202A-\u202E\u2066-\u2069\u061C]/u;
/**
 * A value with no visible grapheme: whitespace, soft hyphen (U+00AD), the zero-width set
 * (U+200B–U+200D), word joiner (U+2060), and BOM (U+FEFF).
 */
const INVISIBLE_ONLY = /^[\s\u00AD\u200B-\u200D\u2060\uFEFF]*$/u;
const graphemes = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
/**
 * Display-text policy (PEC-1/PEC-2). Applies to self-name, member label, personal alias,
 * gate default name, `location.formatted`, and the Dealer ledger reason/reference.
 *
 * NFC-normalize · count length by grapheme cluster · reject C0/C1 controls, bidi
 * override/isolate characters submitted as content, and any value with no visible
 * grapheme. Emoji and mixed scripts stay allowed.
 *
 * @param field Names the offending field on the 422 (contracts.md §11: the error names the
 *   field, never the value). Optional, so the two-argument signature written in
 *   contracts.md §11 type-checks unchanged — see `implementation-notes.md` → P0.
 */
export function normalizeDisplayText(raw, maxGraphemes, field = 'value') {
    if (typeof raw !== 'string')
        throw new ValidationError(field, 'must be a string');
    const normalized = raw.normalize('NFC').trim();
    if (CONTROL_CHARS.test(normalized)) {
        throw new ValidationError(field, 'must not contain control characters');
    }
    if (BIDI_CONTROLS.test(normalized)) {
        throw new ValidationError(field, 'must not contain bidirectional control characters');
    }
    if (INVISIBLE_ONLY.test(normalized)) {
        throw new ValidationError(field, 'must contain at least one visible character');
    }
    let count = 0;
    for (const _ of graphemes.segment(normalized))
        count += 1;
    if (count > maxGraphemes) {
        throw new ValidationError(field, `must be at most ${maxGraphemes} characters`);
    }
    return normalized;
}
/**
 * E.164 normalization — accepts local `05x` for the IL pilot, stores canonical `+972…`.
 *
 * **A number outside `SUPPORTED_COUNTRY_CALLING_CODES` is refused with
 * `UnsupportedCountryError`, and the refusal is a product rule rather than a format one.**
 * Until it existed, any `^\+\d{8,15}$` string normalized cleanly and reached the SMS provider
 * on `POST /auth/otp/start`; the national-shape check below ran only for `+972`, so every
 * other country was validated by length alone.
 *
 * @param field Names the offending field on the 422; see `normalizeDisplayText`.
 */
export function normalizePhone(raw, defaultRegion, field = 'phone') {
    if (typeof raw !== 'string')
        throw new ValidationError(field, 'must be a string');
    // One region today; the parameter is in the contract so a second pilot is an argument
    // rather than a second function.
    void defaultRegion;
    // Separators people actually type. Anything else surviving to the checks below is a
    // real rejection rather than a formatting difference.
    const cleaned = raw.replace(/[\s()\-.\u00A0\u2013\u2014]/g, '');
    if (cleaned.length === 0)
        throw new ValidationError(field, 'must not be empty');
    const rule = 'must be a valid Israeli phone number';
    let candidate;
    if (cleaned.startsWith('+')) {
        candidate = cleaned;
    }
    else if (cleaned.startsWith('00')) {
        candidate = `+${cleaned.slice(2)}`;
    }
    else if (cleaned.startsWith('972')) {
        candidate = `+${cleaned}`;
    }
    else if (cleaned.startsWith('0')) {
        // The national trunk prefix is dropped, never kept: +9720… is not dialable.
        candidate = `+972${cleaned.slice(1)}`;
    }
    else {
        throw new ValidationError(field, rule);
    }
    if (!/^\+\d{8,15}$/.test(candidate))
        throw new ValidationError(field, rule);
    /*
     * **The country allow-list, and it sits between the E.164 shape and the national rule on
     * purpose.** A malformed number is a shape error whatever country it claims, so that answer
     * is given first and an unserved country never masks a typo. A *well-formed* number outside
     * the list is the different answer, and it gets the different error.
     */
    const callingCode = SUPPORTED_COUNTRY_CALLING_CODES.find((code) => candidate.startsWith(`+${code}`));
    if (callingCode === undefined)
        throw new UnsupportedCountryError(field);
    if (callingCode === '972') {
        // IL subscriber numbers are 8 or 9 digits after the country code, and never begin
        // with 0 once the trunk prefix is dropped.
        //
        // **Keyed off the matched code, not off the candidate's own prefix**, so the list above and
        // the national rules here are one question: a code added with no arm of this branch is a
        // country accepted on length alone, which is the hole the list closes.
        if (!/^[1-9]\d{7,8}$/.test(candidate.slice(4)))
            throw new ValidationError(field, rule);
    }
    try {
        return asE164(candidate);
    }
    catch {
        throw new ValidationError(field, rule);
    }
}
//# sourceMappingURL=validation.js.map